/**
 * Investor-Portal — Segment LAGER / Warehousing (#1173 v2.563.0).
 *
 * Hallen-Auslastungs-Geschäft mit dominantem Fixkosten-Block (VGP Park Graz 2 Hallenmiete).
 *
 * Layout:
 *   1. Hero: Hallen-Deckungsgrad (Lager-Umsatz ÷ VGP-Miete)
 *   2. KPI-Tiles: Umsatz, Aufwand, DB, DB-Marge
 *   3. Standort-Tabelle AT/DE/Σ — mit DE-Mini-Hinweis + DE-DB-Validierungs-Warnung
 *   4. Auslastungs-KPIs: m³-Tage, Stellplätze, Umsatz/m³-Tag, Umsatz/Stellplatz
 *   5. Umsatz-Mix Donut: Lagergebühr m³ / Stellplätze / Untervermietung / Outlier / Rest
 *   6. Klumpenrisiko: Top-5 Kunden, Top-3-Anteil
 *   7. Monats-Verlauf: Umsatz / VGP-Fixkosten / DB (period-unabhängig, nur Monate mit Daten)
 *   8. Reconciliation 5720
 *
 * Design strikt nach SKILL-design.md:
 *   Border-Cards, KEINE Shadows, KPI-Werte --text-primary, DM Sans, Farben #338edc/#22D3EE/#6366F1.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Warehouse, Wallet, BarChart3, Percent, TrendingUp, Package, AlertTriangle } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtEurDetail(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);
}
function fmtEurCent(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}
function fmtPct(v) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return `${v.toFixed(1)} %`;
}
function fmtInt(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 }).format(v);
}

const MIX_COLORS = {
  m3_tage: '#338edc',
  stellplaetze: '#22D3EE',
  untervermietung: '#6366F1',
  outlier: '#94A3B8',
  rest: '#E5E7EB',
};

const MIX_LABELS = {
  m3_tage: 'Lagergebühr m³/Tag',
  stellplaetze: 'Paletten-Stellplätze',
  untervermietung: 'Untervermietung',
  outlier: 'Outlier (Versicherung/Falschb.)',
  rest: 'Sonstiges',
};

export default function InvestorSegmentLager() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => {
    // #1184 v2.569.0 — Default = letzter belastbarer Monat
    const mature = defaultMatureMonthState();
    const p = searchParams.get('period') || 'month';
    const y = parseInt(searchParams.get('year') || mature.year, 10);
    const m = parseInt(searchParams.get('month') || mature.month, 10);
    const q = parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10);
    return {
      period: p,
      year: y,
      month: m,
      quarter: q,
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearTrend, setYearTrend] = useState(null);
  const [yearTrendLoading, setYearTrendLoading] = useState(true);
  const [yearTrendError, setYearTrendError] = useState(null);

  // #1184 v2.569.0 — YTD frontend-seitig auf belastbaren Cutoff verkürzen
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/segmente/lager?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); }
        else { setData(json); }
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setYearTrendLoading(true); setYearTrendError(null);
      try {
        const res = await fetch(`${API}/api/investor/segmente/lager/jahrestrend?year=${periodState.year}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setYearTrendError(json.error || `HTTP ${res.status}`); setYearTrend(null); }
        else { setYearTrend(json); }
      } catch (e) { if (!abort) setYearTrendError(e.message); }
      finally { if (!abort) setYearTrendLoading(false); }
    }
    load();
    return () => { abort = true; };
  }, [periodState.year]);

  const kpis = useMemo(() => {
    const gesamt = data?.standorte?.find(s => s.standort === 'GESAMT');
    if (!gesamt) return { umsatz: null, aufwand: null, db: null, marge: null };
    const aufwand = (gesamt.aufwand_lohn || 0) + (gesamt.aufwand_direkt || 0) + (gesamt.aufwand_revshare || 0);
    return { umsatz: gesamt.umsatz, aufwand, db: gesamt.db, marge: gesamt.db_marge_pct };
  }, [data]);

  const mixData = useMemo(() => {
    if (!data?.umsatz_mix) return [];
    const m = data.umsatz_mix;
    const entries = [
      { key: 'm3_tage', value: m.m3_tage?.umsatz || 0, anteil: m.m3_tage?.anteil_pct || 0 },
      { key: 'stellplaetze', value: m.stellplaetze?.umsatz || 0, anteil: m.stellplaetze?.anteil_pct || 0 },
      { key: 'untervermietung', value: m.untervermietung?.umsatz || 0, anteil: m.untervermietung?.anteil_pct || 0 },
      { key: 'outlier', value: m.outlier?.umsatz || 0, anteil: m.outlier?.anteil_pct || 0 },
    ];
    if ((m.rest?.umsatz || 0) > 0.01) {
      entries.push({ key: 'rest', value: m.rest.umsatz, anteil: m.rest.anteil_pct || 0 });
    }
    return entries.filter(e => e.value > 0);
  }, [data]);

  const trendData = useMemo(() => {
    if (!yearTrend?.monats_trend) return [];
    return yearTrend.monats_trend.map(m => ({
      monat: MONTH_LABELS[m.monat - 1],
      Umsatz: Math.round(m.umsatz || 0),
      Aufwand: Math.round(m.aufwand || 0),
      VGPMiete: Math.round(m.vgp_miete || 0),
      DB: Math.round(m.db || 0),
    }));
  }, [yearTrend]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Warehouse className="w-6 h-6 text-[#338edc]" />
            <h1 className="text-2xl font-semibold text-[#1A1A2E]">Segment Lager / Warehousing</h1>
          </div>
          <div className="text-sm text-[#6B7280] mt-1">
            Hallen-Auslastungs-Geschäft mit VGP-Fixkosten-Block — Odoo-Kostenstellen „Lagervolumen- und Vermietung"
          </div>
        </div>
        <PeriodToggle
          period={periodState.period}
          year={periodState.year}
          month={periodState.month}
          quarter={periodState.quarter}
          fromDate={periodState.fromDate}
          toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          Fehler: {error}
        </div>
      )}

      {/* 1. HERO — Hallen-Deckungsgrad (Gesamt-Aufwand + Kostenstruktur-Donut #1174) */}
      <HallenDeckungsgradCard
        hallen={data?.hallen}
        kostenstruktur={data?.kostenstruktur}
        loading={loading}
      />

      {/* 2. KPI-Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Umsatz" value={fmtEur(kpis.umsatz)} sub="Kundenrechnungen Lager" loading={loading}
          Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD"
        />
        <KpiTile
          label="Aufwand" value={fmtEur(kpis.aufwand)} sub="Lohn + Direkt + RevShare" loading={loading}
          Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2"
        />
        <KpiTile
          label="Deckungsbeitrag" value={fmtEur(kpis.db)} sub="Umsatz − Aufwand" loading={loading}
          Icon={BarChart3} iconColor={kpis.db < 0 ? '#EF4444' : '#10B981'} iconTint={kpis.db < 0 ? '#FEE2E2' : '#D1FAE5'}
        />
        <KpiTile
          label="DB-Marge" value={fmtPct(kpis.marge)} sub="DB ÷ Umsatz" loading={loading}
          Icon={Percent} iconColor="#6366F1" iconTint="#E0E7FF"
        />
      </div>

      {/* Reconciliation-Indikator */}
      {data?.reconciliation && (
        <div className={`text-xs px-3 py-2 rounded-lg border flex items-center gap-2 ${
          data.reconciliation.ok === true
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : data.reconciliation.ok === false
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'
        }`}>
          <span className={`w-2 h-2 rounded-full ${
            data.reconciliation.ok === true ? 'bg-emerald-500'
            : data.reconciliation.ok === false ? 'bg-amber-500'
            : 'bg-gray-400'
          }`} />
          {data.reconciliation.ok === true && (
            <span>GuV-Reconciliation OK — DE-RevShare ({fmtEurCent(data.reconciliation.actual)}) deckt sich mit GuV-Konto 5720 auf Lager-Kostenstellen.</span>
          )}
          {data.reconciliation.ok === false && (
            <span>GuV-Reconciliation Abweichung: erwartet {fmtEurCent(data.reconciliation.expected)} · aktuell {fmtEurCent(data.reconciliation.actual)} · Δ {fmtEurCent(data.reconciliation.diff)}</span>
          )}
          {data.reconciliation.ok === null && (
            <span>Reconciliation nicht verfügbar{data.reconciliation.error ? ` (${data.reconciliation.error})` : ''}.</span>
          )}
        </div>
      )}

      {/* 3. Standort-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Standort-Split</h2>
          {data && (
            <span className="text-[11px] text-[#9CA3AF]">
              {data.lines_processed} Odoo-Buchungen · {data.period?.dateFrom} – {data.period?.dateTo}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Standort</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Lohn (Andere)</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Direkt</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">RevShare</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">DB</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Marge</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[#9CA3AF]">Lade Odoo-Daten…</td></tr>
              )}
              {!loading && data?.standorte?.map(s => (
                <tr
                  key={s.standort}
                  className={`border-t border-[#F3F4F6] ${s.standort === 'GESAMT' ? 'bg-[#F8F9FC] font-semibold' : 'hover:bg-[#FAFBFC]'}`}
                >
                  <td className="px-3 py-2 text-[#1A1A2E]">{labelFor(s.standort)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_lohn)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_direkt)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtEurDetail(s.aufwand_revshare)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEurDetail(s.db)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.db_marge_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-[#E5E7EB] bg-[#FAFBFC] flex items-start gap-2 flex-wrap">
          <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded bg-white border border-[#E5E7EB] text-[#6B7280]">
            DE Mini-Position (4,8 %)
          </span>
          {data?.de_db_warning && (
            <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">
              <AlertTriangle className="w-3 h-3 mr-1" /> DE-DB negativ — Datencheck erforderlich (Validierungs-Anker #1172)
            </span>
          )}
          <span className="text-[11px] text-[#9CA3AF]">
            AT strukturell defizitär (Lager ist Vor-Investition für Outbound-Skalierung).
          </span>
        </div>
      </div>

      {/* 4. Auslastungs-KPIs */}
      {data?.auslastung && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Auslastungs-KPIs</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Lager-spezifische Bezugsgrößen — m³-Tage (LV) und Paletten-Stellplätze (STP)
              aus berechneten Rechnungspositionen ({data.auslastung.months_with_data} Monate mit Daten).
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SubKpiTile
              label="m³-Tage gesamt"
              value={fmtInt(data.auslastung.m3_tage_total)}
              sub={`Ø ${fmtInt(data.auslastung.m3_tage_per_month)} / Monat`}
            />
            <SubKpiTile
              label="Umsatz / m³-Tag"
              value={fmtEurCent(data.auslastung.umsatz_per_m3_tag)}
              sub={`Listpreis LV: ${fmtEurCent(0.65)}`}
            />
            <SubKpiTile
              label="Stellplätze gesamt"
              value={fmtInt(data.auslastung.stellplatz_total)}
              sub={`Ø ${fmtInt(data.auslastung.stellplatz_per_month)} / Monat`}
            />
            <SubKpiTile
              label="Umsatz / Stellplatz"
              value={fmtEurCent(data.auslastung.umsatz_per_stellplatz)}
              sub="Mehrere Tage je Buchung"
            />
          </div>
        </div>
      )}

      {/* 5. Umsatz-Mix */}
      {mixData.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Umsatz-Zusammensetzung</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Aufteilung nach Produkt-Kategorie. Untervermietung läuft im Gesamt-Umsatz mit
              (Hallen-Optimierung). „Outlier" = INS-Versicherungsentschädigung + FB1-Falschbuchung.
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={mixData}
                    dataKey="value"
                    nameKey="key"
                    cx="50%" cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {mixData.map(e => <Cell key={e.key} fill={MIX_COLORS[e.key]} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v, name) => [fmtEurDetail(v), MIX_LABELS[name] || name]}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {mixData.map(e => (
                <div key={e.key} className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: MIX_COLORS[e.key] }} />
                  <span className="text-sm text-[#1A1A2E] flex-1">{MIX_LABELS[e.key]}</span>
                  <span className="text-sm font-medium text-[#1A1A2E] tabular-nums">{fmtEur(e.value)}</span>
                  <span className="text-xs text-[#6B7280] tabular-nums w-12 text-right">{fmtPct(e.anteil)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 6. Klumpenrisiko */}
      {data?.klumpenrisiko?.top5?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-[#1A1A2E]">Klumpenrisiko — Top-Kunden</h2>
              <div className="text-[11px] text-[#9CA3AF] mt-0.5">
                {data.klumpenrisiko.kunden_count} Lager-Kunden gesamt
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-[#6366F1]" />
              <span className="text-sm text-[#1A1A2E]">
                <span className="font-semibold tabular-nums">{fmtPct(data.klumpenrisiko.top3_anteil_pct)}</span>
                <span className="text-[#6B7280] ml-1">Top-3-Anteil</span>
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-12">#</th>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Kunde</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {data.klumpenrisiko.top5.map((t, i) => (
                  <tr key={t.partner_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                    <td className="px-3 py-2 text-[#9CA3AF] tabular-nums">{i + 1}</td>
                    <td className="px-3 py-2 text-[#1A1A2E]">{t.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(t.umsatz)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{fmtPct(t.anteil_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 7. Monats-Verlauf */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Monats-Verlauf {periodState.year}</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Voller Jahresverlauf — nur Monate mit Buchungen. Umsatz vs. VGP-Hallenmiete vs. DB.
            </div>
          </div>
          {yearTrendError && (
            <span className="text-[11px] text-red-600">Fehler: {yearTrendError}</span>
          )}
        </div>
        <div className="p-4">
          {yearTrendLoading && !yearTrend && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade Jahres-Trend…</div>
          )}
          {!yearTrendLoading && trendData.length === 0 && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Keine Daten für {periodState.year}.</div>
          )}
          {trendData.length > 0 && reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mb-2">
              {reifeFooter(periodState.year)}
            </div>
          )}
          {trendData.length > 0 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'}
                  />
                  <Tooltip
                    formatter={v => fmtEurDetail(v)}
                    labelStyle={{ color: '#1A1A2E', fontWeight: 600 }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  {/* #1184 v2.569.0 — Vorläufige Monate markieren */}
                  {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                  <Line type="monotone" dataKey="Umsatz" stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="VGPMiete" name="VGP-Miete" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="DB" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo <code>account.analytic.line</code> · Kostenstellen „Lagervolumen- und Vermietung" (id=4, AT-Haupt) +
        „Lagervolumen DE" (id=61) · Standort via <code>kunden.standort_id</code>.
        Lager-Deckungsgrad = Umsatz (ohne Outlier) / |Gesamt-Aufwand|.
        Outlier (INS, FB1) explizit aus dem Zähler ausgeklammert.
      </div>
    </div>
  );
}

const KOSTENSTRUKTUR_COLORS = {
  vgp_miete: '#338edc',
  vgp_verzugszinsen: '#FB923C',
  personalleasing: '#22D3EE',
  energie: '#F59E0B',
  stapler_leasing: '#6366F1',
  material: '#10B981',
  abfall: '#84CC16',
  versicherung: '#EC4899',
  instandhaltung: '#A855F7',
  revshare_de: '#EF4444',
  sonstiges: '#9CA3AF',
};

function HallenDeckungsgradCard({ hallen, kostenstruktur, loading }) {
  // #1185: Hero zeigt AT-Story (Donut ist auch AT-only) — nicht GESAMT (vermischt AT+DE)
  const pct = hallen?.deckungsgrad_at_pct ?? hallen?.deckungsgrad_gesamt_pct;
  const umsatz = hallen?.umsatz_ohne_outlier_at ?? hallen?.umsatz_ohne_outlier;
  const gesamtAufwand = hallen?.gesamt_aufwand_at ?? hallen?.gesamt_aufwand;
  const luecke = hallen?.luecke_at ?? hallen?.luecke_gesamt;
  const clamped = Math.max(0, Math.min(100, pct ?? 0));
  const color = pct == null ? '#9CA3AF' : pct >= 100 ? '#10B981' : pct >= 75 ? '#338edc' : '#F59E0B';

  // #1185: Donut zeigt AT-only (segments_at; Fallback auf segments für Backward-Compat).
  // Filter revshare_de defensiv heraus, falls ältere Endpoint-Response.
  const rawSegments = kostenstruktur?.segments_at || kostenstruktur?.segments || [];
  const segments = rawSegments
    .filter(s => s.key !== 'revshare_de')
    .filter(s => Math.abs(s.anteil_pct) >= 0.5)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const donutData = segments.map(s => ({
    key: s.key,
    label: s.label,
    value: Math.abs(s.value),
    rawValue: s.value,
    anteil: s.anteil_pct,
  }));

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#E5E7EB]">
        <h2 className="text-sm font-semibold text-[#1A1A2E]">Lager-Deckungsgrad — Österreich</h2>
        <div className="text-[11px] text-[#9CA3AF] mt-0.5">
          AT-Lager-Auslastungs-Story: Anteil der AT-Gesamtkosten, der durch AT-Lager-Umsatz (ohne Outlier) gedeckt ist.
          Kostenstruktur zeigt nur AT-Posten — DE/RevShare im Standort-Split-Block unten.
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
        <div className="flex flex-col items-center md:items-start">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Deckungsgrad (AT)</div>
          <div className="text-5xl font-semibold tabular-nums" style={{ color }}>
            {loading ? <span className="inline-block w-32 h-12 rounded bg-[#F3F4F6] animate-pulse" /> : fmtPct(pct)}
          </div>
          <div className="text-[11px] text-[#9CA3AF] mt-1 leading-snug">
            Halle als Vor-Investition für Outbound-Skalierung — Deckungsgrad steigt mit Auslastung.
          </div>
          <div className="w-full mt-3 h-3 bg-[#F3F4F6] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${clamped}%`, backgroundColor: color }} />
          </div>
          {/* #1185 Layout-Fix: vertikaler Stack statt 3-col-grid mit max-w-260 → €-Werte clippen nicht mehr */}
          <div className="flex flex-col gap-2 pt-4 w-full">
            <SubKpiTile
              label="AT-Umsatz"
              value={loading ? '…' : fmtEur(umsatz)}
              sub="ohne Outlier"
            />
            <SubKpiTile
              label="AT-Gesamt-Aufwand"
              value={loading ? '…' : fmtEur(gesamtAufwand)}
              sub={`${kostenstruktur?.lines_processed || 0} Buchungen`}
            />
            <SubKpiTile
              label="Lücke (AT)"
              value={loading ? '…' : fmtEur(luecke)}
              sub={luecke >= 0 ? 'Überdeckt' : 'Nicht voll gedeckt'}
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Kostenstruktur</div>
          {loading ? (
            <div className="w-full h-[220px] rounded bg-[#F3F4F6] animate-pulse" />
          ) : donutData.length === 0 ? (
            <div className="text-sm text-[#9CA3AF]">Keine Aufwand-Buchungen im Zeitraum.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="key"
                      cx="50%" cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {donutData.map(e => <Cell key={e.key} fill={KOSTENSTRUKTUR_COLORS[e.key] || '#9CA3AF'} />)}
                    </Pie>
                    <Tooltip
                      formatter={(v, _name, ctx) => [fmtEurDetail(ctx?.payload?.rawValue ?? -v), ctx?.payload?.label]}
                      contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5">
                {donutData.map(e => (
                  <div key={e.key} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: KOSTENSTRUKTUR_COLORS[e.key] || '#9CA3AF' }} />
                    <span className="text-[#1A1A2E] flex-1 min-w-0 truncate" title={e.label}>{e.label}</span>
                    <span className="font-medium text-[#1A1A2E] tabular-nums whitespace-nowrap flex-shrink-0">{fmtEur(e.rawValue)}</span>
                    <span className="text-[#6B7280] tabular-nums w-12 text-right whitespace-nowrap flex-shrink-0">{fmtPct(e.anteil)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function labelFor(standort) {
  if (standort === 'AT') return 'Österreich';
  if (standort === 'DE') return 'Deutschland';
  if (standort === 'GESAMT') return 'Gesamt';
  return standort;
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }}
            aria-hidden="true"
          >
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

function SubKpiTile({ label, value, sub }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg px-3 py-2.5 bg-[#FAFBFC] flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider text-[#6B7280] leading-tight">{label}</div>
        {sub && <div className="text-[10.5px] text-[#9CA3AF] mt-0.5 truncate">{sub}</div>}
      </div>
      <div className="text-base font-semibold text-[#1A1A2E] tabular-nums whitespace-nowrap">{value}</div>
    </div>
  );
}
